import { SITES } from '../sim/data/sites';
import { region } from '../sim/data/oceans';
import type { WhaleCounts } from '../sim/whales';
import { project } from './worldMap';
import { wholeSeals } from '../sim/systems/seals';
import { displayName, speciesOf } from '../sim/species';

/**
 * The first migrant tracked on the world map (M99, the brief's section 7, "pick a migrant and see its distribution
 * now, its corridors, its seasonal destinations, where it feeds and breeds, its threats"): the Hawaii population of
 * humpback whales, as the simulation counts it in Hawaii's waters (M101, `sim/whales.ts`) and away from them by the
 * feeding ground each feeds on (M106, NOAA's shares), and its ways to its feeding grounds as the sources give them.
 * Which whales are on the way the game does not yet say: the ways are drawn from the sources, not from anything
 * simulated on them. No way runs to Vavaʻu, whose whales are another
 * population, of the Southern Hemisphere (the brief: "Hawaii and Tonga are never joined by one population's routine
 * migration"). Pure, tested in node (`tests/whales.test.ts`).
 */

/**
 * The feeding grounds the Hawaii population's whales were matched to, by the photo-identification matches of the
 * 2004-2006 SPLASH project: "Photographic ID matches between winter and summer areas from the 2004-2006 SPLASH
 * project (from Table 10 in Calambokidis et al. 2008)", the Hawaiʻi column (Wade, Oleson and Young 2021, NOAA Tech.
 * Memo. NMFS-AFSC-430, Table 2, p. 28). Matches, not shares: they fall as the effort did, uncorrected for it
 * ("there are matches from Hawaiʻi to every summer feeding area that was sampled, except for California/Oregon",
 * ibid., p. 6). Each ground's mark a point within it chosen by eye from its name (JUDGED), not a survey's middle,
 * but Southeast Alaska's, the inside waters NOAA's drones measured whales in, "56.93N, -134.50W" (van Aswegen 2025,
 * p. 162).
 */
export const HUMPBACK_GROUNDS: readonly { name: string; latDeg: number; lonDeg: number; matches: number }[] = [
  { name: 'Southeast Alaska', latDeg: 56.93, lonDeg: -134.5, matches: 215 },
  { name: 'the northern Gulf of Alaska', latDeg: 59.5, lonDeg: -147, matches: 124 },
  { name: 'northern British Columbia', latDeg: 53.5, lonDeg: -130.5, matches: 99 },
  { name: 'the Bering Sea', latDeg: 57, lonDeg: -170, matches: 44 },
  { name: 'the western Gulf of Alaska', latDeg: 56.5, lonDeg: -155, matches: 26 },
  { name: 'southern British Columbia and northern Washington', latDeg: 48.5, lonDeg: -124.8, matches: 20 },
  { name: 'the Aleutians', latDeg: 52.5, lonDeg: -172, matches: 4 },
  { name: 'Russia', latDeg: 55, lonDeg: 166.5, matches: 4 },
];

/** Points along the great circle from one place to another, `n` steps, [latitude, longitude] in degrees. */
export function greatCircle(lat1: number, lon1: number, lat2: number, lon2: number, n: number): [number, number][] {
  const rad = Math.PI / 180;
  const v = (lat: number, lon: number): [number, number, number] => [Math.cos(lat * rad) * Math.cos(lon * rad), Math.cos(lat * rad) * Math.sin(lon * rad), Math.sin(lat * rad)];
  const [a, b] = [v(lat1, lon1), v(lat2, lon2)];
  const angle = Math.acos(Math.min(1, Math.max(-1, a[0] * b[0] + a[1] * b[1] + a[2] * b[2])));
  return Array.from({ length: n + 1 }, (_, i) => {
    const t = i / n;
    const [sa, sb] = angle < 1e-9 ? [1 - t, t] : [Math.sin((1 - t) * angle) / Math.sin(angle), Math.sin(t * angle) / Math.sin(angle)];
    const [x, y, z] = [sa * a[0] + sb * b[0], sa * a[1] + sb * b[1], sa * a[2] + sb * b[2]];
    return [Math.atan2(z, Math.hypot(x, y)) / rad, Math.atan2(y, x) / rad];
  });
}

/**
 * The ways from Maui Nui to each feeding ground as lines on the map: great circles, as the whales "follow a roughly
 * straight line from departure to destination" (Darling and Goodwin 2023, *Front. Mar. Sci.* 10: 1223835, p. 2,
 * from satellite tags), each split where it crosses the map's edge; wider for more matches, 1 to 4 px by their
 * square root (a rendering choice).
 */
export function humpbackWays(width: number, height: number): { name: string; px: number; lines: [number, number][][]; end: [number, number] }[] {
  const { latDeg, lonDeg } = SITES.maui_nui.site;
  const most = Math.max(...HUMPBACK_GROUNDS.map((g) => g.matches));
  return HUMPBACK_GROUNDS.map((g) => {
    const lines: [number, number][][] = [[]];
    let last: [number, number] | null = null;
    for (const [la, lo] of greatCircle(latDeg, lonDeg, g.latDeg, g.lonDeg, 48)) {
      const p = project(la, lo, width, height);
      if (last && Math.abs(p[0] - last[0]) > width / 2) lines.push([]);
      (lines[lines.length - 1] as [number, number][]).push(p);
      last = p;
    }
    return { name: g.name, px: 1 + 3 * Math.sqrt(g.matches / most), lines, end: project(g.latDeg, g.lonDeg, width, height) };
  });
}

const n = (x: number): string => Math.round(x).toLocaleString('en-US');

/**
 * The whales away from Hawaiʻi by the feeding ground each feeds on (M106, `sim/whales.ts`), the card's and the
 * sanctuary card's words: each ground's whole whales by the largest remainders (`wholeSeals`), so they add up to those
 * away as shown (M106's review: rounded each alone, 1,618, 381, 22 and 6 made 2,027 of 2,028); which of them are on the
 * way the game does not yet say.
 */
export function feedingGroundsText(c: WhaleCounts): string {
  const whole = wholeSeals(c.away.map((a) => a.whales));
  return `by NOAA's estimates of where they feed: ${c.away.map((a, i) => `${a.ground} ${n(whole[i] as number)}`).join('; ')} (which of them are on the way, the game does not yet say)`;
}

/** The tracking card: the population now, where it breeds and feeds, its journey, its threats, and the player's reach. */
export function humpbackCard(c: WhaleCounts | null): { title: string; rows: [string, string][] } {
  const grounds = HUMPBACK_GROUNDS.map((g) => `${g.matches} in ${g.name}`).join(', ');
  const total = HUMPBACK_GROUNDS.reduce((a, g) => a + g.matches, 0);
  return {
    title: `${displayName(speciesOf('humpback'))}, the Hawaiʻi population`,
    rows: [
      ['Now', c
        ? `${n(c.inHawaii)} in Hawaiʻi's waters, of ${n(c.population)}; the other ${n(c.population - c.inHawaii)} on their feeding grounds or on the way, ${feedingGroundsText(c)}`
        : 'not simulated in this game: its sanctuary has no whales'],
      ['Breeds', 'in Hawaiʻi\'s waters, December to May, most in February and March, few from June to October (NOAA\'s model of its surveys, Becker et al. 2022, Table 9); calves born 19 February to 22 March (the game\'s)'],
      ['Feeds', `off Alaska and British Columbia: of ${total} of its whales photographed in 2004 to 2006 and matched on a feeding ground, ${grounds}; none off California or Oregon (SPLASH, as Wade et al. 2021 give it, Table 2: matches, not shares, as the effort fell)`],
      ['Journey', 'about 4,830 km, made in as little as 36 days (Gabriele et al. 1996, as Bettridge et al. 2015 give it, p. 7), on a roughly straight line (Darling and Goodwin 2023, p. 2): drawn as great circles'],
      ['Threats', 'in Hawaiʻi, deaths and serious injuries reported from ships\' strikes, 3.32 a year, and from entanglement in fishing gear, 4.35 a year (the sum of its five rows), 2016 to 2020, minimums of those reported (NOAA\'s stock assessment, Young et al. 2026, Table 4, "Summary of mortality and serious injury"); not yet counted as causes of death in the game'],
      ['Your reach', 'Maui Nui\'s waters, a part of its winter grounds; its feeding grounds and its way are outside every sanctuary'],
      ['Regions', `${region(SITES.maui_nui.regionId).name} in winter; ${region('north_pacific_high').name} in summer`],
      ['Vavaʻu', 'another population, of the Southern Hemisphere: no way joins them'],
    ],
  };
}
